import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { Link } from 'react-router';
import { ThemeProvider } from 'react-css-themr';
import moment from 'moment';
import MediaQuery from 'react-responsive';
import { can } from '../../../../../services';
import { ResourceDetailsCardElement } from 'components/ResourceCardElements';
import styles from './phaseDetailsBar.scss';
import { Button, FontIcon } from 'react-toolbox';
import DetailsAddress from 'components/DetailsAddress';
import UserNote from 'components/UserNote';
import PhoneLink from 'components/PhoneLink';
import StoreInfo from 'components/StoreInfo';
import MOT from 'components/MOT';
import DueDate from 'components/DueDate';
import ProfitMargin from 'components/ProfitMargin';
import EmailLink from 'components/EmailLink';
import PhaseType from 'types/phase';
import { urls } from 'helpers/url';
import cx from 'classnames';
import { DISPLAY_DATE, CUT_POINT, MULTI_ITEM_AND_SPECIFIC_MAP,
  SPECIAL_SURFACE_TYPES } from 'config/constants';
import VendorLogo from 'components/VendorLogo';
import isIfiPhase from '../phaseShow/components/ifiUtils';

export default class PhaseDetailsBar extends Component {
  constructor(props) {
    super(props);
    this.handleExpand = this.handleExpand.bind(this);
    this.toggleScrollStatus = this.toggleScrollStatus.bind(this);
    this.state = { ...this.derivedStateFromProps(), expand: false, scrolling: false };
  }

  componentDidMount = () => window.addEventListener('scroll', this.toggleScrollStatus, false);

  componentWillUnmount = () => window.removeEventListener('scroll', this.toggleScrollStatus, false);

  derivedStateFromProps = () => {
    const { created_at_default = null, product_type = null,
            surface_type: { name: surfaceType = 'N/A' } = {},
            phase_specific = null, phase_line_items = [] } = this.props.phase || {};
    const motDiff = product_type === 'STK' ? 21 : 28;
    return {
      targetMOTDate: created_at_default ? moment(created_at_default).add(motDiff, 'days').format(DISPLAY_DATE) : null,
      surfaceType,
      detectedSurfaces: SPECIAL_SURFACE_TYPES.includes(surfaceType) ?
        this.findMatchingSurfaces(phase_specific, phase_line_items) : [],
    };
  }

  theme = { ResourceDetailsCE: styles };

  toggleScrollStatus = () => (window.pageYOffset > 0 ?
    this.setState({ scrolling: true }) : this.setState({ scrolling: false }));

  handleExpand = () => this.setState({ expand: !this.state.expand });

  specificsSurfaceMatch = (specifics, matchingSpecifics) =>
    specifics && matchingSpecifics.length > 0 && matchingSpecifics.some(s => specifics[s] === true);

  lineItemSurfaceMatch = (lineItems, itemNumbers, surface) =>
    lineItems.length > 0 && itemNumbers.length > 0 &&
    lineItems.some(li => itemNumbers.includes(li.item_number) ||
      (li.description || '').replace('_', ' ').toLowerCase()
        .includes(surface.replace('_', ' ').toLowerCase()));

  findMatchingSurfaces = (specifics, lineItems) =>
    Object.keys(MULTI_ITEM_AND_SPECIFIC_MAP).filter((key) => {
      const { matchingSpecifics, itemNumbers } = MULTI_ITEM_AND_SPECIFIC_MAP[key];
      return this.specificsSurfaceMatch(specifics, matchingSpecifics) ||
             this.lineItemSurfaceMatch(lineItems, itemNumbers, key);
    }).map(k => k.replace('_', ' '));

  render = () => {
    const { phase: { type = null, customer_name = null, phone_numbers = null, email = null,
                     street_address = null, city = null, state = null, zip_code = null,
                     source = null, project_number = null, sqft = null, spig_delivery = null,
                     effective_due_at = null, ccs_schedulable = null, vendor_eta = null,
                     contractor_notes = null, ims_link = null, estimator = null, store = null,
                     store: { workroom_id: workroomId = '', workroom_name: workroomName = '',
                              ccs_schedulable: storeCcsSchedulable = null,
                              vendor: storeVendor = null } = {},
                     responsible_persons: {
                       installer_name = '',
                       installer_supervisor_name = '',
                       estimator_supervisor_name = '',
                     } = {} } = {},
                     phase = null, role, position = '', mvw = {},
                     canViewWorkroom } = this.props;
    const { expand, targetMOTDate, surfaceType, detectedSurfaces } = this.state;
    const supervisorNames = [installer_supervisor_name, estimator_supervisor_name]
      .filter(Boolean)
      .filter((name, idx, arr) => arr.indexOf(name) === idx);
    const supervisorDisplay = supervisorNames.length ? supervisorNames.join(', ') : '--';
    const hasMultiSurfaces = detectedSurfaces.length > 0;
    return (<ThemeProvider theme={this.theme}>
      <div
        className={cx(styles.phaseDetailsBar, { [styles.scrolling]: this.state.scrolling })}
      >
        <MediaQuery minWidth={CUT_POINT}>
          <div className={styles.phaseDue}>
            {<VendorLogo
              workroomName={workroomName}
              source={source}
              classMap={styles}
            />}
          </div>
        </MediaQuery>
        <div
          className={`${styles.ResourceDetails} ${expand ? styles['ResourceDetails-expanded'] : ''}`}
        >
          <ResourceDetailsCardElement wrap title={`${surfaceType}${hasMultiSurfaces ? ` - ${detectedSurfaces.join(', ')}` : ''}`}>
            <h1 className={styles.phaseType}>{type}</h1>
            {sqft && `${sqft} sqft`}
            <br />
            {spig_delivery && <span style={{ color: 'red' }}>SPIG DELIVERY</span>}
          </ResourceDetailsCardElement>
          <ResourceDetailsCardElement wrap title="Customer">
            {customer_name}<br />
            {phone_numbers.length && phone_numbers.map(phone => <PhoneLink
              key={phone.number}
              className={styles.phone} number={phone.number}
            />)}
          </ResourceDetailsCardElement>
          <ResourceDetailsCardElement wrap title="Address">
            <DetailsAddress
              address={street_address}
              city={city}
              state={state}
              zipCode={zip_code}
            />
            {email && <div>
              <EmailLink email={email}>{email}</EmailLink>
            </div>}
          </ResourceDetailsCardElement>
          <ResourceDetailsCardElement title="project number">
            <a href={ims_link} target="_blank" rel="noopener noreferrer">
              {project_number}
            </a>
            {surfaceType !== 'Pro Direct' && (type === 'Assessment' ||
              (ccs_schedulable && type === 'Installation' && storeCcsSchedulable)) ?
                <div style={{ color: 'red' }}>Phase CCS Schedulable</div> : '' }
            <br />
            {phase.lrrp_required && <span style={{ color: '#cc0000' }}>LRRP REQUIRED</span>}
          </ResourceDetailsCardElement>
          {estimator && <ResourceDetailsCardElement title="estimator">
            <Link
              className={styles.viewLink}
              to={`/estimators/${estimator.id}`}
              target="_blank"
            >{estimator.name}</Link>
          </ResourceDetailsCardElement>}
          {can(role, 'view', 'SUPERVISOR_INFO', {
            position,
            mvw,
          }) && storeVendor === 'IFI' && (
            <Fragment>
              <ResourceDetailsCardElement title="installer">
                {installer_name || '--'}
              </ResourceDetailsCardElement>
              <ResourceDetailsCardElement title="coordinator">
                {supervisorDisplay}
              </ResourceDetailsCardElement>
            </Fragment>
          )}
          {targetMOTDate && <ResourceDetailsCardElement title="Target MOT Date">
            {targetMOTDate}
          </ResourceDetailsCardElement>}
          {store && <ResourceDetailsCardElement title={source === 'IFI' ? 'District' : 'workroom'}>
            {canViewWorkroom ? <Link
              className={styles.viewLink}
              to={urls.workroomFromPhaseDetails(workroomId)}
              target="_blank"
            >{workroomName}</Link> :
            <div>{workroomName}</div>}
          </ResourceDetailsCardElement>}
          <ResourceDetailsCardElement wrap title="Store Info">
            {store && <StoreInfo store={store} linkToMaps={['installer', 'crew'].includes(role)} />}
          </ResourceDetailsCardElement>
          {type !== 'Assessment' && !isIfiPhase({ source }) && <ResourceDetailsCardElement wrap title="Vendor ETA">
            { vendor_eta ? moment(vendor_eta, DISPLAY_DATE).format(DISPLAY_DATE) : '--'}
          </ResourceDetailsCardElement>}
          <ResourceDetailsCardElement wrap title="Contractor Notes" className={styles.notes}>
            {contractor_notes ? <UserNote className={styles.notesContainer}>
              {contractor_notes}</UserNote> : '--'}
          </ResourceDetailsCardElement>
          <Button onClick={this.handleExpand} className={styles.expandButton}>
            {expand ? <FontIcon>keyboard_arrow_up</FontIcon>
              : <FontIcon>keyboard_arrow_down</FontIcon>}
          </Button>
        </div>
        <MediaQuery minWidth={CUT_POINT}>
          <div className={styles.phaseDue}>
            {can(role, 'view', 'due_date') && <div className={styles.phaseDueLine}>
              <DueDate date={effective_due_at} isPhaseDetails />
            </div>}
            {can(role, 'view', 'created_at_default') && <div className={styles.phaseDueLine}>
              <MOT phase={phase} />
            </div>}
            {type !== 'Assessment' && can(role, 'view', 'profit_margin') && <div className={styles.phaseDueLine}>
              <ProfitMargin phase={phase} />
            </div>}
          </div>
        </MediaQuery>
        <MediaQuery maxWidth={CUT_POINT - 1}>
          <div className={styles.mobilePhaseDue}>
            <div className={styles.mobilePhaseDueContent}>
              {can(role, 'view', 'due_date') && <div className={styles.phaseDueLine}>
                <DueDate date={effective_due_at} isPhaseDetails />
              </div>}
              {can(role, 'view', 'created_at_default') && <div className={styles.phaseDueLine}>
                <MOT phase={phase} />
              </div>}
            </div>
            <div className={styles.logoMobile}>
              <VendorLogo
                workroomName={workroomName}
                source={source}
                classMap={styles}
              />
            </div>
          </div>
        </MediaQuery>
      </div>
    </ThemeProvider>);
  }
}

PhaseDetailsBar.propTypes = {
  phase: PhaseType.isRequired,
  role: PropTypes.string.isRequired,
  position: PropTypes.string.isRequired,
  mvw: PropTypes.object,
  canViewWorkroom: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/components/phaseDetailsBar.jsx